import * as Yup from 'yup';
import { Course, CourseLevelsEnum } from 'src/@types/Course';

// Course form ⇄ API body (plan R1 C2/C3/C4/C6).
//
// The edit form used to PATCH every field plus the course's `sections` copied
// from the list row, so an Order change could overwrite lessons saved in
// another tab, and any unrelated legacy value the API refuses (an empty
// certificate URL, a 2-letter keyword) blocked the save. An EDIT now sends
// only the fields that changed — never `sections`, `rating` or `duration` —
// and the API rules are checked only on those fields. A CREATE checks every
// field. One order range everywhere: whole numbers 0-9999, lower shows first
// (be COURSE_ORDER_MIN / COURSE_ORDER_MAX).

export const COURSE_ORDER_MIN = 0;
export const COURSE_ORDER_MAX = 9999;
export const ORDER_RANGE_TEXT = 'Order must be a whole number from 0 to 9999';
export const ORDER_HELPER_TEXT = '0-9999, lower shows first';
export const KEYWORD_MIN_TEXT = 'Each keyword needs at least 3 characters';
export const CERTIFICATE_REQUIRED_TEXT = 'Certificate is required';

export type CourseFormValues = {
  name: string;
  name_ar: string;
  category: string;
  certificateUrl: string;
  briefVideo: string;
  image: string;
  icon: string;
  description: string;
  description_ar: string;
  instructor: string;
  keywords: string[];
  keywords_ar: string[];
  track: string;
  active: boolean;
  isFree: boolean;
  level: CourseLevelsEnum | string;
  package: string;
  passScore: number | string | undefined;
  slug: string;
  // Text, so a stored 0 shows as "0" (RHFTextField blanks a numeric 0).
  order: string | number | undefined;
  price: number | string | undefined;
  price_usd: number | string | undefined;
  seo_title: string;
  seo_title_ar: string;
  seo_description: string;
  seo_description_ar: string;
};

export type CourseFormKey = keyof CourseFormValues;

export const COURSE_FORM_KEYS: CourseFormKey[] = [
  'name',
  'name_ar',
  'category',
  'certificateUrl',
  'briefVideo',
  'image',
  'icon',
  'description',
  'description_ar',
  'instructor',
  'keywords',
  'keywords_ar',
  'track',
  'active',
  'isFree',
  'level',
  'package',
  'passScore',
  'slug',
  'order',
  'price',
  'price_usd',
  'seo_title',
  'seo_title_ar',
  'seo_description',
  'seo_description_ar',
];

const NUMBER_KEYS: CourseFormKey[] = ['order', 'price', 'price_usd', 'passScore'];
const BOOLEAN_KEYS: CourseFormKey[] = ['active', 'isFree'];
const LIST_KEYS: CourseFormKey[] = ['keywords', 'keywords_ar'];
// Left out when empty: the API wants 3+ characters for an Arabic name, and an
// empty slug is not a slug.
const OMIT_WHEN_EMPTY: CourseFormKey[] = ['name_ar', 'slug'];

const strings = (list: unknown): string[] =>
  Array.isArray(list) ? list.filter((k) => typeof k === 'string') : [];

const idOf = (v: any): string => (v && typeof v === 'object' ? v._id || v.id || '' : v || '');

/** The form's values for a stored course (or an empty create form). */
export function courseFormValues(course?: Course): CourseFormValues {
  return {
    name: course?.name || '',
    name_ar: course?.name_ar || '',
    category: idOf(course?.category) || idOf((course?.track as any)?.category),
    certificateUrl: course?.certificateUrl || '',
    briefVideo: course?.briefVideo || '',
    image: course?.image || '',
    icon: course?.icon || '',
    description: course?.description || '',
    description_ar: course?.description_ar || '',
    instructor: idOf(course?.instructor),
    keywords: strings(course?.keywords),
    keywords_ar: strings(course?.keywords_ar),
    track: idOf(course?.track),
    active: course?.active || false,
    isFree: course?.isFree || false,
    level: course?.level || CourseLevelsEnum.BASIC,
    package: idOf(course?.package),
    passScore: course?.passScore ?? 85,
    slug: course?.slug || '',
    order: typeof course?.order === 'number' ? String(course.order) : '',
    price: course?.price || 0,
    price_usd: course?.price_usd || 0,
    seo_title: course?.seo_title || '',
    seo_title_ar: course?.seo_title_ar || '',
    seo_description: course?.seo_description || '',
    seo_description_ar: course?.seo_description_ar || '',
  };
}

/** '' / null / not a number → undefined; otherwise the number. */
export function parseNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : undefined;
}

export const parseOrder = parseNumber;

function sameValue(key: CourseFormKey, a: unknown, b: unknown): boolean {
  if (NUMBER_KEYS.includes(key)) return parseNumber(a) === parseNumber(b);
  if (BOOLEAN_KEYS.includes(key)) return Boolean(a) === Boolean(b);
  if (LIST_KEYS.includes(key)) return JSON.stringify(strings(a)) === JSON.stringify(strings(b));
  return String(a ?? '') === String(b ?? '');
}

/** The form fields whose value differs from the one the form opened with. */
export function changedCourseKeys(
  values: Partial<CourseFormValues>,
  initial: Partial<CourseFormValues>
): CourseFormKey[] {
  return COURSE_FORM_KEYS.filter((key) => !sameValue(key, values[key], initial[key]));
}

// ----------------------------------------------------------------------
// Rules = the API's (CreateCourseDto). In edit mode they run on the changed
// fields only, so an untouched legacy value never blocks a save.

const emptyToUndefined = (v: unknown, original: unknown) =>
  original === '' || original === null || original === undefined ? undefined : v;

const urlExpression = /^(ftp|http|https):\/\/[^ "]+$/;

const paidPrice = (field: Yup.NumberSchema<number | undefined>, text: string) =>
  field.when('isFree', {
    is: (val: boolean) => !val,
    then: Yup.number().moreThan(0, text),
  });

function fieldRules(): Record<CourseFormKey, Yup.AnySchema> {
  return {
    name: Yup.string().min(3).required('Course title is required'),
    name_ar: Yup.string(),
    category: Yup.string().required('category is required'),
    certificateUrl: Yup.string().required(CERTIFICATE_REQUIRED_TEXT),
    briefVideo: Yup.string()
      .min(3)
      .required('brief Video Url title is required')
      .matches(urlExpression, 'briefVideo Url must be a valid url'),
    image: Yup.string(),
    icon: Yup.string(),
    description: Yup.string().min(3).required('description is Required'),
    description_ar: Yup.string(),
    instructor: Yup.string().required('instructor is required'),
    keywords: Yup.array()
      .of(Yup.string().min(3, KEYWORD_MIN_TEXT))
      .min(1, 'Add at least one keyword'),
    keywords_ar: Yup.array().of(Yup.string().min(3, KEYWORD_MIN_TEXT)),
    track: Yup.string().required('track is required'),
    active: Yup.boolean(),
    // A paid course (isFree = false) must carry a positive EGP price AND a
    // positive USD price. The backend treats the EGP `price` as the canonical
    // "is it paid" signal, and charges price_usd*50 for non-EG buyers — so a 0
    // in either field makes the course unsellable / charged as $0.
    isFree: Yup.boolean(),
    level: Yup.string().required('Course Level is Required'),
    package: Yup.string().required('package is Required'),
    passScore: Yup.number().transform(emptyToUndefined).min(0).max(100),
    slug: Yup.string(),
    order: Yup.number()
      .transform(emptyToUndefined)
      .typeError(ORDER_RANGE_TEXT)
      .integer(ORDER_RANGE_TEXT)
      .min(COURSE_ORDER_MIN, ORDER_RANGE_TEXT)
      .max(COURSE_ORDER_MAX, ORDER_RANGE_TEXT),
    price: paidPrice(
      Yup.number().transform(emptyToUndefined).min(0),
      'A paid course needs an EGP price greater than 0 (or mark it as Free).'
    ),
    price_usd: paidPrice(
      Yup.number().transform(emptyToUndefined).min(0),
      'A paid course needs a USD price greater than 0 (or mark it as Free).'
    ),
    seo_title: Yup.string(),
    seo_title_ar: Yup.string(),
    seo_description: Yup.string(),
    seo_description_ar: Yup.string(),
  };
}

/**
 * The fields the form checks: all of them on create; on edit the changed ones,
 * plus both prices when "Free" changed (a course made paid needs its prices).
 */
export function courseValidationKeys(
  values: Partial<CourseFormValues>,
  initial: Partial<CourseFormValues>,
  isEdit: boolean
): CourseFormKey[] {
  if (!isEdit) return COURSE_FORM_KEYS;
  const keys = changedCourseKeys(values, initial);
  if (keys.includes('isFree')) {
    (['price', 'price_usd'] as CourseFormKey[]).forEach((k) => {
      if (!keys.includes(k)) keys.push(k);
    });
  }
  return keys;
}

export function courseSchema(keys: CourseFormKey[] = COURSE_FORM_KEYS) {
  const rules = fieldRules();
  const shape: Record<string, Yup.AnySchema> = {};
  keys.forEach((key) => {
    shape[key] = rules[key];
  });
  return Yup.object().shape(shape);
}

// ----------------------------------------------------------------------
// Bodies

const bodyValue = (key: CourseFormKey, value: unknown): unknown => {
  if (NUMBER_KEYS.includes(key)) return parseNumber(value);
  if (BOOLEAN_KEYS.includes(key)) return Boolean(value);
  if (LIST_KEYS.includes(key)) return strings(value);
  return typeof value === 'string' ? value : value ?? '';
};

// title / title_ar are required by the API but not collected in the form:
// they follow the course name.
const titleFields = (values: Partial<CourseFormValues>) => ({
  title: values.name || '',
  title_ar: values.name_ar || values.name || '',
});

/**
 * The PATCH body of an edit: only the changed fields (an emptied Order, Arabic
 * name or slug is left out = unchanged), plus title / title_ar when a name
 * changed. Never `sections`, `rating` or `duration`.
 */
export function buildCoursePatch(
  values: Partial<CourseFormValues>,
  initial: Partial<CourseFormValues>
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  changedCourseKeys(values, initial).forEach((key) => {
    const value = bodyValue(key, values[key]);
    if (value === undefined) return;
    if (OMIT_WHEN_EMPTY.includes(key) && String(value).trim() === '') return;
    body[key] = value;
  });
  if ('name' in body || 'name_ar' in body) Object.assign(body, titleFields(values));
  return body;
}

/** The POST body of a create: every field (empty optional ones left out). */
export function buildCourseCreate(values: Partial<CourseFormValues>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  COURSE_FORM_KEYS.forEach((key) => {
    const value = bodyValue(key, values[key]);
    if (value === undefined) return;
    if (value === '') return;
    body[key] = value;
  });
  // A new course with no Order shows first, as the form always did.
  if (body.order === undefined) body.order = 0;
  return { ...body, ...titleFields(values), sections: [], rating: 1, duration: 0 };
}

// ----------------------------------------------------------------------
// Keywords (immutable: the form's initial values must never change under it)

export function addKeyword(list: unknown, raw: unknown): string[] {
  const current = strings(list);
  const word = typeof raw === 'string' ? raw.trim() : '';
  if (!word || current.includes(word)) return current;
  return [...current, word];
}

export function removeKeyword(list: unknown, index: number): string[] {
  return strings(list).filter((_, i) => i !== index);
}
